import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  CreditCard, Check, Loader2, Star, Sparkles, Droplets, ArrowLeft
} from 'lucide-react';
import PublicLegalFooter from '@/components/PublicLegalFooter';
import CheckoutConsent from '@/components/CheckoutConsent';

export default function ClientPlans() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [identifiedVehicle, setIdentifiedVehicle] = useState(null);
  const [availablePlans, setAvailablePlans] = useState({});
  const [vehicleTypeDisplay, setVehicleTypeDisplay] = useState('');
  // Consentimento obrigatório de checkout (Termos + Política de Reembolso)
  const [checkoutAccepted, setCheckoutAccepted] = useState(false);
  const [consentVersions, setConsentVersions] = useState({ terms: '1.0', refund: '1.0' });

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    
    // Recuperar veículo identificado
    const vehicleData = sessionStorage.getItem('identified_vehicle');
    const typeDisplay = sessionStorage.getItem('vehicle_type_display');
    
    if (vehicleData) {
      const vehicle = JSON.parse(vehicleData);
      setIdentifiedVehicle(vehicle);
      setVehicleTypeDisplay(typeDisplay || '');
      
      // SEMPRE buscar planos da API
      fetchPlansFromAPI(vehicle.vehicle_type);
    }
    
    fetchVehicles();
  }, [user, navigate]);

  const fetchPlansFromAPI = async (vehicleType) => {
    try {
      const res = await axios.get(`${API}/vehicles/plans-by-type/${vehicleType}`);
      console.log('PLANOS RECEBIDOS DA API:', res.data);
      setAvailablePlans(res.data.plans || {});
    } catch (error) {
      console.error('Erro ao buscar planos:', error);
      setAvailablePlans({});
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/client/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(res.data.vehicles);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (vehicles.length === 0) {
      toast.error('Cadastre um veículo primeiro');
      navigate('/client/vehicle');
      return;
    }

    if (!checkoutAccepted) {
      toast.error('Para continuar, aceite os Termos de Uso e a Política de Reembolso.');
      return;
    }

    setSelectedPlan(planId);
    setLoading(true);

    // Redirecionar diretamente para pagamento usando plan_id
    const vehicleId = identifiedVehicle?.id || vehicles[vehicles.length - 1].id;
    const plan = availablePlans[planId];
    
    navigate('/client/payment', {
      state: {
        vehicle_id: vehicleId,
        plan_id: plan?.plan_key || planId,
        plan_name: plan?.name,
        plan_price: plan?.price,
        accepted_terms_version: consentVersions.terms,
        accepted_refund_policy_version: consentVersions.refund,
        accepted_checkout_at: new Date().toISOString()
      }
    });
  };

  // Ordenar planos por preço (menor para maior)
  const plansToDisplay = Object.entries(availablePlans)
    .sort(([, a], [, b]) => (a.price || 0) - (b.price || 0));

  // Ícone do plano baseado no tier
  const getPlanIcon = (planKey, plan) => {
    const name = plan?.name?.toLowerCase() || '';
    if (planKey.includes('premium') || name.includes('premium')) return <Star className="w-5 h-5" />;
    if (planKey.includes('intermediate') || name.includes('intermediário')) return <Sparkles className="w-5 h-5" />;
    return <Droplets className="w-5 h-5" />;
  };

  // Cor do gradiente baseado no tier
  const getPlanColor = (planKey, plan) => {
    const name = plan?.name?.toLowerCase() || '';
    if (planKey.includes('premium') || name.includes('premium')) return 'from-amber-500 to-amber-600';
    if (planKey.includes('intermediate') || name.includes('intermediário')) return 'from-primary to-primary/80';
    return 'from-slate-600 to-slate-700';
  };

  // Verificar se é o plano mais popular (intermediate ou is_popular)
  const isPopularPlan = (planKey, plan) => {
    // Verificar campo is_popular do banco
    if (plan.is_popular === true) return true;
    // Verificar pela key
    if (planKey.includes('intermediate')) return true;
    // Verificar pelo nome do plano
    if (plan.name?.toLowerCase().includes('intermediário')) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center bg-primary/10 p-3 rounded-xl mb-4">
            <CreditCard className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2" data-testid="plans-title">
            Escolha seu Plano
          </h1>
          <p className="text-muted-foreground">
            Selecione o plano ideal para você e comece a economizar
          </p>
        </div>
        
        {/* Veículo identificado */}
        {identifiedVehicle && vehicleTypeDisplay && (
          <div className="mb-8 text-center">
            <div className="inline-block bg-white border rounded-lg px-6 py-3 shadow-sm">
              <p className="text-sm text-muted-foreground mb-1">Planos disponíveis para:</p>
              <p className="text-lg font-semibold" data-testid="identified-vehicle-info">
                {identifiedVehicle.brand} {identifiedVehicle.model} {identifiedVehicle.year ? identifiedVehicle.year : ''} – {vehicleTypeDisplay}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Placa: {identifiedVehicle.plate}
              </p>
            </div>
          </div>
        )}
        
        {/* Planos */}
        {plansToDisplay.length > 0 ? (
          <>
            {/* Consentimento obrigatório de checkout (Termos + Reembolso) */}
            <CheckoutConsent
              accepted={checkoutAccepted}
              onAcceptedChange={setCheckoutAccepted}
              onVersionsChange={setConsentVersions}
            />

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {plansToDisplay.map(([key, plan]) => {
                const isPopular = isPopularPlan(key, plan);
                const isPremium = key.includes('premium');
                
                return (
                  <Card 
                    key={key} 
                    className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${
                      isPopular ? 'border-2 border-primary shadow-lg scale-105' : ''
                    } ${selectedPlan === key ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  >
                    {/* Badge MAIS POPULAR */}
                    {isPopular && (
                      <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-xs font-bold text-center py-1">
                        MAIS POPULAR
                      </div>
                    )}
                    
                    <div className={`p-6 ${isPopular ? 'pt-8' : ''}`}>
                      {/* Header do plano */}
                      <div className="flex items-center gap-2 mb-4">
                        <div className={`bg-gradient-to-r ${getPlanColor(key, plan)} text-white p-2 rounded-lg`}>
                          {getPlanIcon(key, plan)}
                        </div>
                        <h3 className="text-xl font-bold">{plan.name}</h3>
                      </div>
                      
                      {/* Preço */}
                      <div className="mb-4">
                        <p className="text-4xl font-bold text-primary">
                          R$ {plan.price}
                          <span className="text-lg font-normal text-muted-foreground">/mês</span>
                        </p>
                      </div>
                      
                      {/* Descrição */}
                      <p className="text-sm text-muted-foreground mb-4">
                        {plan.description}
                      </p>
                      
                      {/* Benefícios */}
                      <ul className="space-y-2 mb-6">
                        {plan.limits?.complete > 0 && (
                          <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span>{plan.limits.complete} Lavagem{plan.limits.complete > 1 ? 'ns' : ''} completa{plan.limits.complete > 1 ? 's' : ''}</span>
                          </li>
                        )}
                        {plan.limits?.ducha > 0 && (
                          <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span>{plan.limits.ducha} Ducha{plan.limits.ducha > 1 ? 's' : ''} simples</span>
                          </li>
                        )}
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Agendamento via app</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Suporte prioritário</span>
                        </li>
                      </ul>
                      
                      {/* Botão */}
                      <Button 
                        onClick={() => handleSubscribe(plan.plan_key || key)}
                        disabled={loading || !checkoutAccepted}
                        className={`w-full ${isPopular ? '' : 'bg-slate-800 hover:bg-slate-900'}`}
                        data-testid={`subscribe-${key}-btn`}
                      >
                        {loading && selectedPlan === key ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Processando...
                          </>
                        ) : (
                          'Selecionar Plano'
                        )}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
            
            {/* Info adicional */}
            <div className="text-center text-sm text-muted-foreground mb-6">
              <p>Os planos acima são exclusivos para veículos do tipo: <strong>{vehicleTypeDisplay}</strong></p>
              <p className="mt-2">O tipo do veículo é identificado automaticamente pela placa/chassi</p>
            </div>
          </>
        ) : (
          <Card className="p-8 text-center mb-8">
            {identifiedVehicle ? (
              <>
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-muted-foreground">Carregando planos disponíveis...</p>
              </>
            ) : (
              <>
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  Cadastre um veículo para ver os planos disponíveis
                </p>
                <Button onClick={() => navigate('/client/vehicle')}>
                  Cadastrar Veículo
                </Button>
              </>
            )}
          </Card>
        )}
        
        {/* Botão voltar */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/client/dashboard')} 
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Dashboard
          </Button>
        </div>

        {/* Rodapé público de compliance jurídico */}
        <div className="pt-6 mt-2 border-t border-slate-200">
          <PublicLegalFooter />
        </div>
      </div>
    </div>
  );
}
